import * as React from 'react';
import styled from 'styled-components';
import { animated, useSpring } from '@react-spring/web';

import { useSwipeable } from 'react-swipeable';

import { GetProperty } from '../../../../../__types__/generated/GetProperty';
import { SiteContext } from '../../../../utils/context/SiteContext';
import { useSlideshow } from '../../../../utils/hooks/useSlideshow';

import { ProgressBar } from './Progress';
import { Slide } from './Slide';

import { useNoBodyScroll } from '../../../../utils/hooks/useBodyNoScroll';
import { PlayButton } from './PlayButton';
import { CloseButton } from './CloseButton';
import { NavButton } from './NavButton';
import { GalleryButton } from './GalleryButton';
import { GalleryOverview } from './GalleryOverview';
import { useTimeout } from '../../../../utils/hooks/useTimeout';
import { GetPropertyTop } from '../../../../../__types__/generated/GetPropertyTop';

interface IGalleryContext {
  galleryOpen: boolean;
  galleryOverviewOpen: boolean;
  setGalleryOpen: (value: boolean) => void;
  setGalleryOverviewOpen: (value: boolean) => void;
  togglePlay: () => void;
  select: (index: number) => void;
  pause: () => void;
}

export const GalleryContext = React.createContext<IGalleryContext>({
  galleryOpen: false,
  galleryOverviewOpen: false,
  setGalleryOpen: () => {},
  setGalleryOverviewOpen: () => {},
  togglePlay: () => {},
  select: () => {},
  pause: () => {},
});

export const Gallery: React.FC<{ data: GetPropertyTop }> = React.memo(
  ({ data }) => {
    const { galleryOpen, setGalleryOpen } = React.useContext(SiteContext);
    const [autoplay, setAutoplay] = React.useState(false);
    const [galleryOverviewOpen, setGalleryOverviewOpen] = React.useState(false);
    const [showPlaceholder, setShowPlaceholder] = React.useState(false);

    useNoBodyScroll({
      noScroll: galleryOpen,
    });

    React.useEffect(() => {
      setAutoplay(galleryOpen);
      if (galleryOpen) {
        setGalleryOverviewOpen(false);
      }
    }, [galleryOpen]);

    useTimeout(() => {
      if (data?.getProperty?.images?.list?.length === 0) {
        setShowPlaceholder(true);
      }
    }, 1000);

    const {
      slides,
      current,
      handlePrev,
      handleNext,
      select,
      togglePlay,
      pause,
      isPlaying,
    } = useSlideshow({
      items: data?.getProperty?.images?.list || [],
      banners: [],
      lazyLoad: 2,
      autoplay: autoplay,
      duration: 3500,
    });

    const handlers = useSwipeable({
      onSwiped: ({ dir }: { dir: string }) => {
        if (dir === 'Right') {
          handlePrev();
        } else if (dir === 'Left') {
          handleNext();
        }
      },
    });

    const navigationAnimatedProps = useSpring({
      delay: 600,
      from: { opacity: 0 },
      to: { opacity: galleryOpen ? 1 : 0 },
    });

    const noImagePlaceholderProps = useSpring({
      from: { opacity: 0 },
      to: { opacity: showPlaceholder && !data?.getProperty?.sold ? 1 : 0 },
    });

    return (
      <GalleryContext.Provider
        value={{
          galleryOpen,
          setGalleryOpen,
          galleryOverviewOpen,
          select,
          setGalleryOverviewOpen,
          togglePlay,
          pause,
        }}
      >
        <StyledGalleryWrapper>
          <GalleryTop
            current={current}
            slideCount={slides.length}
            playing={galleryOpen && isPlaying}
          />
          {galleryOpen && galleryOverviewOpen ? (
            <GalleryOverview data={data} />
          ) : null}
          <StyledSlideWrapper {...handlers}>
            {slides && slides.length
              ? slides.map((item, index: number) => (
                  <Slide
                    slideRef={item.ref}
                    key={`slide_${index}`}
                    text={item.text}
                    current={current}
                    numberOfSlides={slides.length}
                    show={current === index}
                    galleryOpen={galleryOpen}
                    galleryOverviewOpen={galleryOverviewOpen}
                    attr={item.attr}
                    clickable={false}
                    onClick={() => {}}
                  />
                ))
              : null}
            <NoImagePlaceholder style={noImagePlaceholderProps}>
              <svg
                viewBox="0 0 324 324"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M165,211.9 C155.6,207.9 153.6,204.3 153.6,195.1 L153.6,170.5 L173,170.5 C196.2,170.5 211.7,160.3 211.7,141 C211.7,121.7 196.2,111.5 172.9,111.5 L124,111.5 L124,111.8 C130.6,115.5 132.1,119.9 132.1,129.4 L132.1,194.4 C132.1,203.9 130.7,208.2 124,212 L124,212.3 L165,212.3 L165,211.9 Z M153.7,115.3 L166.2,115.3 C182.7,115.3 190.4,125.8 190.4,141.1 C190.4,156.4 182.8,166.9 166.2,166.9 L153.7,166.9 L153.7,115.3 Z M213.2,83.2 L219.6,99.6 L220.7,99.6 L227.1,83.2 L232.3,98.8 L237.1,98.8 L228.1,71.8 L227,71.8 L220.2,89.1 L213.4,71.8 L212.3,71.8 L203.3,98.8 L208,98.8 L213.2,83.2 Z M0.1,0.1 L0.1,323.6 L323.6,323.6 L323.6,0.1 L0.1,0.1 Z M316.2,316.2 L7.5,316.2 L7.5,7.5 L316.2,7.5 L316.2,316.2 Z"></path>
              </svg>
            </NoImagePlaceholder>
          </StyledSlideWrapper>

          {galleryOpen && !galleryOverviewOpen ? (
            <NavButton
              style={navigationAnimatedProps}
              onClick={handlePrev}
              position="left"
            />
          ) : null}
          {galleryOpen && !galleryOverviewOpen ? (
            <NavButton
              style={navigationAnimatedProps}
              onClick={handleNext}
              position="right"
            />
          ) : null}
        </StyledGalleryWrapper>
      </GalleryContext.Provider>
    );
  }
);

const StyledGalleryWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  user-select: none;
`;

interface IGalleryTop {
  current: number;
  playing: boolean;
  slideCount: number;
}

const GalleryTop: React.FC<IGalleryTop> = React.memo(
  ({ current, playing, slideCount }) => {
    const {
      galleryOpen,
      setGalleryOpen,
      galleryOverviewOpen,
      setGalleryOverviewOpen,
      togglePlay,
      pause,
    } = React.useContext(GalleryContext);

    const animatedProps = useSpring({
      delay: !galleryOpen ? 0 : 650,
      from: { display: 'none', transform: 'translate3d(0, -4px, 0)' },
      to: {
        display: galleryOpen ? 'block' : 'none',
        transform: playing
          ? 'translate3d(0, 20px, 0)'
          : 'translate3d(0, -4px, 0)',
      },
    });

    return (
      <StyledGalleryTop style={animatedProps}>
        <ProgressBar current={current} count={slideCount} run={playing} />
        <Navigation>
          <PlayButton playing={playing} onClick={togglePlay} />
          <div className="slide-nav-column">
            <GalleryButton
              open={galleryOverviewOpen}
              onClick={() => {
                if (galleryOverviewOpen) {
                  setGalleryOverviewOpen(false);
                } else {
                  setGalleryOverviewOpen(true);
                  pause();
                }
              }}
            />
            <CloseButton
              className="close-button"
              onClick={() => {
                setGalleryOpen(false);
                pause();
              }}
            />
          </div>
        </Navigation>
      </StyledGalleryTop>
    );
  }
);

const StyledGalleryTop = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 0 20px;
  width: 100%;

  .slide-nav-column {
    display: flex;
    align-items: center;

    .close-button {
      margin-left: 10px;
    }
  }
`;

const StyledSlideWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const NoImagePlaceholder = styled(animated.div)`
  width: 100%;
  height: 100%;
  padding: 0 30px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
    max-width: 300px;
    max-height: 300px;

    path {
      fill: ${({ theme }) => theme.top.imagePlaceholder};
    }
  }
`;
